<template>
  <div class="vm-image-list-results" @click="handlePaginationClick">
    <div v-if="isLoading" class="vm-image-list-results__overlay">
      <div class="vm-image-list-results__loader">
        <span class="vm-image-list-results__spinner" aria-hidden="true"></span>
        <span>Loading VM image list...</span>
      </div>
    </div>

    <AppPagination
      :pagination="pagination"
      aria-label="VM image pagination"
      key-prefix="vm-image-page"
    />

    <div class="vm-detail-shell" :id="shellId">
      <table class="table table-striped table-bordered table-condensed vm-detail-table-wrap vm-image-table">
        <thead>
          <tr>
            <th class="vm-image-col vm-image-col--os"><div class="text-center">OS</div></th>
            <th class="vm-image-col vm-image-col--name"><div class="text-center">VM Image SnapShot Name</div></th>
            <th class="vm-image-col vm-image-col--size hidden-phone"><div class="text-center">Size</div></th>
            <th class="vm-image-col vm-image-col--date hidden-tablet"><div class="text-center">Uploaded Date</div></th>
            <th class="vm-image-col vm-image-col--owner"><div class="text-center">Owner</div></th>
            <th class="vm-image-col vm-image-col--actions vm-image-actions-col"><div class="text-center">Actions</div></th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="!paginatedRows.length && !isLoading">
            <td colspan="6" class="text-center vm-image-list-results__empty">No VM images found.</td>
          </tr>
          <tr v-for="row in paginatedRows" :key="`vm-image-${row.id}`">
            <td><div class="text-center"><AppOsIcon :symbol="row.os_icon_symbol" /></div></td>
            <td>
              <div class="text-left vm-image-name-cell">
                <a
                  :href="row.detail_page_url"
                  class="vm-detail-link"
                  @click.prevent.stop="handleOpenVmImageDetail(row.detail_url)"
                >{{ row.name }}</a>
              </div>
            </td>
            <td class="hidden-phone"><div class="text-center">{{ row.size }}</div></td>
            <td class="hidden-tablet vm-image-date-cell"><div class="text-center">{{ row.updated_at }}</div></td>
            <td><div class="text-center">{{ row.owner }}</div></td>
            <td class="vm-image-actions-col">
              <div class="text-center vm-image-actions" v-if="row.actions">
                <div class="vm-image-more-actions">
                  <button
                    type="button"
                    class="vm-image-more-actions__gear"
                    :aria-expanded="openMenuId === row.id ? 'true' : 'false'"
                    @click.stop="toggleMenu(row.id, $event)"
                  >
                    <AppIcon name="settings" />
                    <AppIcon name="chevron-down" class="vm-image-more-actions__caret" />
                  </button>
                  <div
                    v-if="openMenuId === row.id"
                    class="vm-image-more-actions__menu"
                    :style="menuStyle"
                    @click.stop
                  >
                    <a v-if="row.actions.download_url" :href="row.actions.download_url" class="vm-image-more-actions__item hidden-tablet">
                      <AppIcon name="download" />
                      <span>Download</span>
                    </a>
                    <a :href="row.actions.migration_url" class="vm-image-more-actions__item">
                      <AppIcon name="disk" />
                      <span>{{ row.actions.migration_label }}</span>
                    </a>
                    <a :href="row.actions.edit_url" class="vm-image-more-actions__item">
                      <AppIcon name="edit" />
                      <span>Edit</span>
                    </a>
                    <button
                      v-if="row.actions.can_delete"
                      type="button"
                      class="vm-image-more-actions__item vm-image-more-actions__item--danger"
                      @click="handleDelete(row)"
                    >
                      <AppIcon name="delete" />
                      <span>Delete</span>
                    </button>
                    <span
                      v-else
                      class="vm-image-more-actions__item vm-image-more-actions__item--disabled"
                      :title="row.actions.delete_disabled_message"
                    >
                      <AppIcon name="delete" />
                      <span>Delete</span>
                    </span>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <aside
        :id="`${shellId}-panel`"
        class="vm-detail-panel"
        :style="vmImageDetailPanelStyle"
        :aria-hidden="vmImageDetail.open && vmImageDetail.mode === 'panel' ? 'false' : 'true'"
      >
        <VmImageDetailPanel
          :open="vmImageDetail.open && vmImageDetail.mode === 'panel'"
          :title="vmImageDetail.title"
          :detail-data="vmImageDetail.detailData"
          @close="closeVmImageDetail"
        />
      </aside>
      <VmImageDetailModal
        :modal-id="vmImageDetailModalId"
        :open="vmImageDetail.open && vmImageDetail.mode === 'modal'"
        :title="vmImageDetail.title"
        :detail-data="vmImageDetail.detailData"
        @close="closeVmImageDetail"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AppIcon from './AppIcon.vue'
import AppPagination from './AppPagination.vue'
import AppOsIcon from './AppOsIcon.vue'
import VmImageDetailModal from './VmImageDetailModal.vue'
import VmImageDetailPanel from './VmImageDetailPanel.vue'

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({ rows: [], shell_id: 'vm-image-detail-shell' })
  },
  initialLoading: {
    type: Boolean,
    default: false
  }
})

const data = ref(props.initialData || { rows: [], shell_id: 'vm-image-detail-shell' })
const isLoading = ref(!!props.initialLoading)
const currentPage = ref(1)
const shellId = computed(() => data.value.shell_id || 'vm-image-detail-shell')
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
const openMenuId = ref(null)
const menuStyle = ref({})
const perPage = 20
const vmImageDetail = ref({
  open: false,
  mode: 'panel',
  title: '',
  detailData: null
})
const vmImageDetailPanelStyle = ref({})
const vmImageDetailModalId = computed(() => `show_vm_image_detail_${shellId.value}`)

function sccShowToast(level, message) {
  window.sccStatusShell?.sccShowToast?.(level, message)
}

const pagination = computed(() => {
  const totalCount = (data.value.rows || []).length
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage))
  const safeCurrentPage = Math.min(currentPage.value, totalPages)

  return {
    current_page: safeCurrentPage,
    total_pages: totalPages,
    prev_page: safeCurrentPage > 1 ? safeCurrentPage - 1 : null,
    next_page: safeCurrentPage < totalPages ? safeCurrentPage + 1 : null
  }
})

const paginatedRows = computed(() => {
  const offset = (pagination.value.current_page - 1) * perPage
  return (data.value.rows || []).slice(offset, offset + perPage)
})

watch(
  () => props.initialData,
  (nextValue) => {
    data.value = nextValue || { rows: [], shell_id: 'vm-image-detail-shell' }
    currentPage.value = 1
  }
)

watch(
  () => props.initialLoading,
  (nextValue) => {
    isLoading.value = !!nextValue
  }
)

function mountNestedVue() {
  if (window.sccMountVueComponents) {
    window.sccMountVueComponents()
  }
}

function handlePaginationClick(event) {
  const link = event.target.closest('.pagination a[data-page]')
  if (!link) {
    return
  }

  event.preventDefault()
  const page = Number(link.dataset.page)
  if (!Number.isFinite(page) || page < 1) {
    return
  }

  currentPage.value = page
}

function toggleMenu(rowId, event) {
  if (openMenuId.value === rowId) {
    openMenuId.value = null
    menuStyle.value = {}
    return
  }

  const trigger = event?.currentTarget
  const rect = trigger?.getBoundingClientRect()
  if (!rect) {
    openMenuId.value = rowId
    menuStyle.value = {}
    return
  }

  const menuWidth = 182
  const menuHeight = 148
  const gap = 8
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let left = rect.right - menuWidth
  let top = rect.bottom + gap

  if (left + menuWidth > viewportWidth - 12) left = viewportWidth - menuWidth - 12
  if (left < 12) left = 12
  if (top + menuHeight > viewportHeight - 12) top = rect.top - menuHeight - gap
  if (top < 12) top = 12

  openMenuId.value = rowId
  menuStyle.value = { position: 'fixed', top: `${top}px`, left: `${left}px` }
}

function closeMenu() {
  openMenuId.value = null
  menuStyle.value = {}
}

function shouldUseVmImageDetailPanel() {
  const shell = document.getElementById(shellId.value)
  const panel = document.getElementById(`${shellId.value}-panel`)
  if (!shell || !panel || window.innerWidth <= 991) {
    return false
  }

  const shellRect = shell.getBoundingClientRect()
  const availableRight = Math.max(0, window.innerWidth - shellRect.right - 18)
  return availableRight >= 280
}

function layoutVmImageDetailPanel() {
  const shell = document.getElementById(shellId.value)
  const table = shell ? shell.querySelector('.vm-detail-table-wrap') : null
  const footer = document.querySelector('.footer')
  const gap = 18
  if (!shell) {
    return
  }

  const shellRect = shell.getBoundingClientRect()
  const shellWidth = shell.offsetWidth
  const footerHeight = footer ? footer.offsetHeight : 0
  const viewportAvailableHeight = Math.max(320, window.innerHeight - Math.max(shellRect.top, 0) - footerHeight - gap)
  const panelHeight = Math.max(table ? table.offsetHeight : shell.offsetHeight, viewportAvailableHeight)

  if (window.innerWidth <= 991) {
    vmImageDetailPanelStyle.value = {
      left: '0px',
      width: `${shellWidth}px`,
      minHeight: `${panelHeight}px`,
      height: `${panelHeight}px`
    }
    return
  }

  const minPreferredWidth = 400
  const maxPreferredWidth = 560
  const availableRight = Math.max(0, window.innerWidth - shellRect.right - gap)
  const preferredWidth = Math.min(maxPreferredWidth, Math.max(minPreferredWidth, availableRight))
  let left = shellWidth + gap

  if (availableRight < preferredWidth) {
    left = Math.max(24, left - (preferredWidth - availableRight))
  }

  vmImageDetailPanelStyle.value = {
    left: `${left}px`,
    width: `${preferredWidth}px`,
    minHeight: `${panelHeight}px`,
    height: `${panelHeight}px`
  }
}

function closeVmImageDetail() {
  const shell = document.getElementById(shellId.value)
  if (shell) {
    shell.classList.remove('vm-detail-shell-open')
  }

  vmImageDetail.value = {
    open: false,
    mode: vmImageDetail.value.mode,
    title: '',
    detailData: null
  }
}

function rerenderVmImageDetailForViewport() {
  if (!vmImageDetail.value.open) {
    return
  }

  const usePanel = shouldUseVmImageDetailPanel()
  const mode = usePanel ? 'panel' : 'modal'
  vmImageDetail.value = {
    ...vmImageDetail.value,
    mode
  }

  const shell = document.getElementById(shellId.value)
  if (shell) {
    if (mode === 'panel') {
      shell.classList.add('vm-detail-shell-open')
      layoutVmImageDetailPanel()
    } else {
      shell.classList.remove('vm-detail-shell-open')
    }
  }
}

function handleOpenVmImageDetail(url) {
  if (!url) {
    return
  }

  requestJson(url, { method: 'GET' })
    .then((payload) => {
      const usePanel = shouldUseVmImageDetailPanel()
      const mode = usePanel ? 'panel' : 'modal'
      vmImageDetail.value = {
        open: true,
        mode,
        title: payload?.title || '',
        detailData: payload?.detail_data || null
      }

      const shell = document.getElementById(shellId.value)
      if (shell) {
        if (mode === 'panel') {
          shell.classList.add('vm-detail-shell-open')
          layoutVmImageDetailPanel()
        } else {
          shell.classList.remove('vm-detail-shell-open')
        }
      }
    })
    .catch((error) => {
      sccShowToast('danger', error?.message || 'Failed to load VM image detail.')
    })
}

async function handleDelete(row) {
  const actions = row.actions || {}
  if (!actions.destroy_url) {
    return
  }

  if (actions.destroy_confirm_message && !window.confirm(actions.destroy_confirm_message)) {
    return
  }

  try {
    const payload = await requestJson(actions.destroy_url, { method: 'DELETE' })
    if (payload && payload.level && payload.message) {
      sccShowToast(payload.level, payload.message)
    }

    data.value = {
      ...data.value,
      rows: (data.value.rows || []).filter((entry) => String(entry.id) !== String(row.id))
    }
    closeMenu()
  } catch (error) {
    sccShowToast('danger', error.message || 'Failed to delete VM image.')
  }
}

async function requestJson(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase()
  const headers = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(options.headers || {})
  }
  const fetchOptions = {
    method,
    headers,
    credentials: 'same-origin'
  }

  if (csrfToken && method !== 'GET' && method !== 'HEAD') {
    headers['X-CSRF-Token'] = csrfToken
  }

  const response = await fetch(url, fetchOptions)
  const text = await response.text()
  let payload = null

  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!response.ok) {
    throw new Error((payload && payload.message) || text || 'Request failed.')
  }

  return payload
}

onMounted(() => {
  nextTick(mountNestedVue)
  document.addEventListener('click', handleDocumentClick)
  window.addEventListener('resize', rerenderVmImageDetailForViewport)
  document.addEventListener('keyup', handleDocumentKeyup)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
  window.removeEventListener('resize', rerenderVmImageDetailForViewport)
  document.removeEventListener('keyup', handleDocumentKeyup)
})

watch(data, () => {
  isLoading.value = false
  nextTick(mountNestedVue)
})

function handleDocumentClick(event) {
  if (!event.target.closest('.vm-image-more-actions__menu') && !event.target.closest('.vm-image-more-actions__gear')) {
    closeMenu()
  }
}

function handleDocumentKeyup(event) {
  if (event.key !== 'Escape') {
    return
  }

  closeVmImageDetail()
}
</script>

<style scoped>
.vm-image-list-results {
  position: relative;
  min-height: 120px;
}

.vm-image-list-results__overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 250, 253, 0.82);
  backdrop-filter: blur(1px);
}

.vm-image-list-results__loader {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border: 1px solid rgba(130, 150, 175, 0.24);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  color: #36506a;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 16px 34px rgba(122, 141, 164, 0.16);
}

.vm-image-list-results__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(116, 143, 171, 0.22);
  border-top-color: #4d6d8f;
  border-radius: 999px;
  animation: vm-image-list-results-spin 0.9s linear infinite;
}

.vm-image-list-results__empty {
  color: #64748b;
  padding: 24px 12px !important;
}

.vm-image-table {
  table-layout: fixed;
}

.vm-image-col--os {
  width: 56px;
}

.vm-image-col--name {
  width: 240px;
}

.vm-image-col--size {
  width: 132px;
}

.vm-image-col--date {
  width: 176px;
}

.vm-image-col--owner {
  width: 110px;
}

.vm-image-col--actions {
  width: 74px;
}

.vm-image-name-cell {
  text-align: left;
}

.vm-image-date-cell {
  white-space: nowrap;
}

.vm-image-more-actions {
  position: relative;
}

.vm-image-more-actions__gear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 44px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d4dde8;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #eef3f8 100%);
  color: #4a5f75;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
}

.vm-image-more-actions__caret {
  font-size: 11px;
  opacity: 0.75;
}

.vm-image-more-actions__menu {
  z-index: 60;
  min-width: 182px;
  padding: 8px;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 20px 38px rgba(15, 23, 42, 0.16);
}

.vm-image-more-actions__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #425466;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
}

.vm-image-more-actions__item:hover {
  background: #eff4f9;
  color: #27445f;
  text-decoration: none;
}

.vm-image-more-actions__item--danger {
  color: #9f1d2d;
}

.vm-image-more-actions__item--danger:hover {
  background: #fff1f2;
  color: #7f1722;
}

.vm-image-more-actions__item--disabled {
  color: #93a1b1;
  cursor: not-allowed;
}

@keyframes vm-image-list-results-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
